import './underwriting.create.submission.scss';

import * as Yup from 'yup';

import {
  AttachmentUploadInput,
  Attachments,
  SubmissionInput,
  SubmissionType,
  UserGroup,
  useGetBrokerOptions,
  useGetBrokerageAddress,
  useGetEntities,
  useGetEntitiesBrokerAgencies,
  useGetUsers,
  useUpsertSubmissionMutation,
} from '@converge/graphql';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import {
  ConvergeSectionTitle,
  CountryFormSearchableSelectField,
  DialogLayout,
  FileUploader,
  FormContainerWithHeader,
  FormNumberField,
  FormNumberInputAdornmentField,
  FormRichTextField,
  FormSearchableSelectField,
  FormViewValueField,
  GridLayout,
  IUploadFile,
  IUploadOnDropProps,
  IndustrySectorFormSearchableSelectField,
  NewConvergeButton,
  NewDateField,
  NewFormTextField,
  NewFromSelectField,
  PaperLayout,
  RegionFormSearchableSelectField,
  StackLayout,
} from '../../../presentation';
import {
  CreateSubmissionField,
  DEFAULT_PRODUCT_TYPE,
  PRODUCT_TYPE_OPTIONS,
  ProductType,
  UNITED_STATES_OF_AMERICA_LABEL,
  getCreateSubmissionFieldProps,
  isUnitedStatesOfAmericaLabel,
} from '@converge/data';
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useFormContext,
} from 'react-hook-form';
import {
  addDays,
  addYear,
  capitalizeFirstLetter,
  checkPositiveNumber,
  scrollToFirstError,
} from '@converge/utils';
import config, { convergeLogger } from '@converge/config';
import {
  createSubmissionFieldsSchema,
  stringRequired,
} from './create.submission.validation.schema';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useConvergeNavigation, useConvergeSnackBar } from '../../../contexts';

import { ApolloError } from '@apollo/client';
import { LoadingButton } from '@mui/lab';
import { getUserOptions } from '../../submission';
import moment from 'moment';
import { useAuthContext } from '../../auth/require-auth';
import { yupResolver } from '@hookform/resolvers/yup';

export type CreateSubmissionFields = SubmissionInput & {
  [CreateSubmissionField.PartnerUuid]: string | null;
  [CreateSubmissionField.ProductType]: ProductType;

  annualRevenue: number | null;
  projectedRevenue: number | null;
  projectedForeignRevenue: number | null;
  foreignRevenue: number | null;
  ratedRevenueOverride: number | null;
};

const submissionTypes = Object.entries(SubmissionType).map(
  ([label, value]) => ({ label, value })
);
const yupString = stringRequired(CreateSubmissionField.RegionUuid);

export const UnderwritingCreateSubmission = () => {
  const { user } = useAuthContext();
  const [isInsuredInUnitedStates, setIsInsuredInUnitedStates] =
    useState<boolean>(true);
  const [showEntityAlreadyExistsModal, setShowEntityAlreadyExistsModal] =
    useState<boolean>(false);
  const [showMessage, setShowMessage] = useState('');
  const [
    entityAlreadyExistsConfirmationToken,
    setEntityAlreadyExistsConfirmationToken,
  ] = useState<null | string>(null);

  const formMethods = useForm<CreateSubmissionFields>({
    resolver: yupResolver(
      createSubmissionFieldsSchema.concat(
        Yup.object().shape({
          [CreateSubmissionField.RegionUuid]: isInsuredInUnitedStates
            ? yupString.uuid()
            : yupString,
        })
      )
    ),
    defaultValues: {
      [CreateSubmissionField.ReceivedBy]: user?.user.username,

      [CreateSubmissionField.Type]: SubmissionType.Primary,
      [CreateSubmissionField.ProductType]: DEFAULT_PRODUCT_TYPE,

      [CreateSubmissionField.CountryUuid]: UNITED_STATES_OF_AMERICA_LABEL,
      [CreateSubmissionField.PolicyStartDate]: addDays(new Date(), 0),
      [CreateSubmissionField.PartnerUuid]: 'No Partner',
    },
    shouldUseNativeValidation: false,
  });

  const { upsertSubmission, loading: isCreatingSubmission } =
    useUpsertSubmissionMutation({}, 'SubmissionQueue');
  const { notify } = useConvergeSnackBar();
  const navigate = useConvergeNavigation();

  const onSubmitHandler = useCallback(
    (confirmationToken?: string): SubmitHandler<CreateSubmissionFields> =>
      async (fields) => {
        let submissionResponse, submissionResponseErrors;

        const { policyStartDate, policyEndDate } = fields;

        formMethods?.clearErrors(CreateSubmissionField.PolicyEndDate);

        if (
          policyStartDate &&
          policyEndDate &&
          moment(policyEndDate).isBefore(moment(policyStartDate))
        ) {
          formMethods?.setError(CreateSubmissionField.PolicyEndDate, {
            type: 'manual',
            message:
              'Policy expiration date must be greater than or equal to policy effective date.',
          });
          return;
        }
        try {
          const { data, errors } = await upsertSubmission({
            variables: {
              details: {
                ...createSubmissionFieldsToVariables(fields),
                confirmationToken,
              },
            },
          });
          submissionResponse = data;
          submissionResponseErrors = errors;
        } catch (cause) {
          console.error(`Failed to create submission: ${cause}`);

          if (cause instanceof ApolloError) {
            submissionResponseErrors = cause.graphQLErrors;
          }
        }

        const errorMessage = submissionResponseErrors?.[0]?.message;

        if (errorMessage || !submissionResponse) {
          notify(
            `Failed to create submission: ${
              errorMessage || 'No data returned by the API'
            }`,
            {
              variant: 'error',
            }
          );
          return;
        }

        if (
          submissionResponse.upsertSubmission.__typename ===
          'EntityAlreadyExists'
        ) {
          setShowEntityAlreadyExistsModal(true);
          setShowMessage(submissionResponse.upsertSubmission.message);
          setEntityAlreadyExistsConfirmationToken(
            submissionResponse.upsertSubmission.confirmationToken
          );
          return;
        }

        if (
          submissionResponse.upsertSubmission.__typename === 'ValidationErrors'
        ) {
          notify(
            `Invalid submission: ${submissionResponse.upsertSubmission.message}`,
            {
              variant: 'warning',
            }
          );

          return;
        }

        if (submissionResponse.upsertSubmission.__typename === 'Submission') {
          notify('Successfully created submission', {
            variant: 'success',
          });

          navigate.underwriting();
          return;
        }

        notify(`An unknown error occurred while creating submission`, {
          variant: 'error',
        });
      },
    [navigate, notify, upsertSubmission, formMethods]
  );

  return (
    <PaperLayout className="wrapper-style" sx={{ p: 2, mt: 2 }}>
      <DialogLayout
        open={showEntityAlreadyExistsModal}
        onClose={() => {
          setShowEntityAlreadyExistsModal(false);
          setShowMessage('');
        }}
        onCloseIconClick={() => {
          setShowEntityAlreadyExistsModal(false);
          setShowMessage('');
        }}
        showCloseButton
        body={<p className="message">{showMessage}</p>}
        header={'Entity has one or more existing submissions'}
        footer={
          <Stack>
            <Typography sx={{ paddingBottom: '5px' }}>{``}</Typography>
            <Box className="confirmation-modal-submission-footer">
              <Button onClick={() => setShowEntityAlreadyExistsModal(false)}>
                NO
              </Button>
              <LoadingButton
                onClick={() => {
                  setShowEntityAlreadyExistsModal(false);
                  onSubmitHandler(entityAlreadyExistsConfirmationToken ?? '')(
                    formMethods.getValues()
                  );
                }}
                variant="contained"
                autoFocus
                loading={isCreatingSubmission}
              >
                Yes
              </LoadingButton>
            </Box>
          </Stack>
        }
      />
      <FormProvider {...formMethods}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            formMethods.handleSubmit(onSubmitHandler())();
            scrollToFirstError(formMethods);
          }}
          className="wrapper-style"
        >
          <StackLayout direction="column" overflow="auto">
            <StackLayout className="form-layout" direction="column">
              <UnderwritingCreateSubmissionInsuredSection
                setIsInsuredInUnitedStates={setIsInsuredInUnitedStates}
                isInsuredInUnitedStates={isInsuredInUnitedStates}
              />
              <UnderwritingCreateSubmissionBrokerSection />
              <UnderwriterSection />
              <UnderwritingCreateSubmissionRequestedPolicySection />

              <UnderwritingCreateSubmissionSubmissionSection />
              {config.featureFlags.enableDiagnosticMode && (
                <>
                  <ConvergeSectionTitle>Diagnostics</ConvergeSectionTitle>
                  <TextField
                    multiline
                    minRows={5}
                    value={JSON.stringify(
                      createSubmissionFieldsToVariables(
                        formMethods.getValues()
                      ),
                      undefined,
                      2
                    )}
                  />
                </>
              )}
            </StackLayout>
            <StackLayout
              justifyContent="flex-end"
              className="create-submission-footer"
            >
              <Box>
                <NewConvergeButton
                  type="submit"
                  label="CREATE"
                  loading={isCreatingSubmission}
                  onClick={() => {
                    formMethods.handleSubmit(onSubmitHandler())();
                    scrollToFirstError(formMethods);
                  }}
                />
              </Box>
            </StackLayout>
          </StackLayout>
        </form>
      </FormProvider>
    </PaperLayout>
  );
};

const UnderwritingCreateSubmissionInsuredSection = ({
  setIsInsuredInUnitedStates,
  isInsuredInUnitedStates,
}: {
  setIsInsuredInUnitedStates: (flag: boolean) => void;
  isInsuredInUnitedStates: boolean;
}) => {
  const formMethods = useFormContext();
  const [selectedCountry, setSelectedCountry] = useState('US');
  const [RegionName, setRegionName] = useState<string>('State');

  return (
    <>
      <Box className="submission-row">
        <NewFormTextField
          {...getCreateSubmissionFieldProps(CreateSubmissionField.LegalName)}
          autoComplete={false}
          required
          placeholder="Enter Legal Name"
          className="create-submission-filed-size"
        />

        <NewFormTextField
          {...getCreateSubmissionFieldProps(CreateSubmissionField.TradingName)}
          autoComplete={false}
          placeholder="Enter DBA"
          className="create-submission-filed-size"
        />
        <NewFormTextField
          {...getCreateSubmissionFieldProps(CreateSubmissionField.Domain)}
          required
          placeholder="Enter Domain"
          className="create-submission-filed-size"
        />
      </Box>
      <Box className="submission-row">
        <NewFormTextField
          {...getCreateSubmissionFieldProps(CreateSubmissionField.Street1)}
          required
          autoComplete={false}
          placeholder="1234 Main St"
          className="create-submission-filed-size"
        />
        <NewFormTextField
          {...getCreateSubmissionFieldProps(CreateSubmissionField.Street2)}
          autoComplete={false}
          placeholder="Suite 123"
          className="create-submission-filed-size"
        />
        <NewFormTextField
          {...getCreateSubmissionFieldProps(CreateSubmissionField.City)}
          autoComplete={false}
          required
          placeholder="Enter City"
          className="create-submission-filed-size"
        />
      </Box>
      <Box className="submission-row">
        <Box className="create-submission-filed-size">
          <RegionFormSearchableSelectField
            {...getCreateSubmissionFieldProps(
              CreateSubmissionField.RegionUuid,
              capitalizeFirstLetter(RegionName)
            )}
            minDropdownWidth="430px"
            maxDropdownWidth="600px"
            autoComplete={false}
            required
            label="STATE"
            selectedCountry={selectedCountry}
            setRegionName={setRegionName}
            placeholder="Select State"
          />
        </Box>
        <NewFormTextField
          {...getCreateSubmissionFieldProps(CreateSubmissionField.PostalCode)}
          autoComplete={false}
          required
          placeholder="Enter Postal Code"
          className="create-submission-filed-size"
        />
        <Box className="create-submission-filed-size">
          <CountryFormSearchableSelectField
            minDropdownWidth="430px"
            maxDropdownWidth="600px"
            {...getCreateSubmissionFieldProps(
              CreateSubmissionField.CountryUuid
            )}
            autoComplete={false}
            required
            placeholder="Select Country"
            onChangeOption={(listOption) => {
              setSelectedCountry(listOption?.data?.iso2Code || '');

              if (listOption) {
                const isUnitedStates = isUnitedStatesOfAmericaLabel(
                  listOption.label
                );
                if (isUnitedStates !== isInsuredInUnitedStates) {
                  setIsInsuredInUnitedStates(isUnitedStates);
                }
                formMethods.setValue(
                  CreateSubmissionField.CountryUuid,
                  listOption?.value
                );
              } else {
                setIsInsuredInUnitedStates(false);
              }
              formMethods.setValue(CreateSubmissionField.RegionUuid, '');
            }}
          />
        </Box>
      </Box>
      <Box className="submission-row">
        <NewFormTextField
          {...getCreateSubmissionFieldProps(
            CreateSubmissionField.ContactFirstName
          )}
          placeholder="Enter First Name"
          className="create-submission-filed-size"
        />
        <NewFormTextField
          {...getCreateSubmissionFieldProps(
            CreateSubmissionField.ContactLastName
          )}
          placeholder="Enter Last Name"
          className="create-submission-filed-size"
        />
        <NewFormTextField
          {...getCreateSubmissionFieldProps(
            CreateSubmissionField.ContactEmailAddress
          )}
          placeholder="Enter Email Address"
          className="create-submission-filed-size"
        />

        <NewFormTextField
          {...getCreateSubmissionFieldProps(
            CreateSubmissionField.ContactPhoneNumber
          )}
          placeholder="Enter Phone Number"
          className="create-submission-filed-size"
        />
      </Box>
      <Box className="submission-row">
        <Box className="create-submission-filed-size">
          <FormNumberField
            {...getCreateSubmissionFieldProps(
              CreateSubmissionField.NumEmployees
            )}
            thousandSeparator=","
            isAllowed={(values) => checkPositiveNumber(values)}
            placeholder="Enter Number of Employees"
          />
        </Box>
        <Box className="create-submission-filed-size">
          <FormNumberField
            {...getCreateSubmissionFieldProps(
              CreateSubmissionField.EstablishedYear
            )}
            isAllowed={(values) => checkPositiveNumber(values)}
            placeholder="Enter Year Established"
          />
        </Box>
        <Box className="create-submission-filed-size">
          <IndustrySectorFormSearchableSelectField
            {...getCreateSubmissionFieldProps(
              CreateSubmissionField.IndustrySector
            )}
            minDropdownWidth="430px"
            maxDropdownWidth="600px"
            placeholder="Select Industry Sector"
            onChangeOption={(listOption) => {
              formMethods.setValue(
                CreateSubmissionField.IndustrySector,
                listOption?.value,
                {
                  shouldValidate: true,
                  shouldDirty: true,
                  shouldTouch: true,
                }
              );
            }}
          />
        </Box>
      </Box>
      <Box className="submission-row">
        <Box className="create-submission-filed-size">
          <FormNumberInputAdornmentField
            {...getCreateSubmissionFieldProps(
              CreateSubmissionField.RatedRevenueOverride
            )}
            required
            adornmentText="$"
            thousandSeparator=","
            isAllowed={(values) => checkPositiveNumber(values)}
            placeholder="0"
          />
        </Box>
        <Box className="create-submission-filed-size">
          <FormNumberInputAdornmentField
            {...getCreateSubmissionFieldProps(
              CreateSubmissionField.ProjectDomesticRevenue
            )}
            adornmentText="$"
            thousandSeparator=","
            isAllowed={(values) => checkPositiveNumber(values)}
            placeholder="0"
          />
        </Box>
        <Box className="create-submission-filed-size">
          <FormNumberInputAdornmentField
            {...getCreateSubmissionFieldProps(
              CreateSubmissionField.CurrentDomesticRevenue
            )}
            required
            adornmentText="$"
            thousandSeparator=","
            isAllowed={(values) => checkPositiveNumber(values)}
            placeholder="0"
          />
        </Box>
      </Box>
      <Box className="submission-row">
        <Box className="create-submission-filed-size">
          <FormNumberInputAdornmentField
            {...getCreateSubmissionFieldProps(
              CreateSubmissionField.CurrentForeignRevenue
            )}
            adornmentText="$"
            thousandSeparator=","
            isAllowed={(values) => checkPositiveNumber(values)}
            placeholder="0"
          />
        </Box>
        <Box className="create-submission-filed-size">
          <FormNumberInputAdornmentField
            {...getCreateSubmissionFieldProps(
              CreateSubmissionField.ProjectForeignRevenue
            )}
            adornmentText="$"
            thousandSeparator=","
            isAllowed={(values) => checkPositiveNumber(values)}
            placeholder="0"
          />
        </Box>
        <Box className="create-submission-filed-size"></Box>
      </Box>
    </>
  );
};

const UnderwritingCreateSubmissionBrokerSection = () => {
  const { setValue, watch } = useFormContext();
  const brokerageUuid = watch(CreateSubmissionField.BrokerageUuid);
  // const brokerAgentUuid = watch(CreateSubmissionField.BrokerAgentUuid);

  const { brokerAgencies, loading: brokerageLoading } =
    useGetEntitiesBrokerAgencies({
      pageSize: 500,
    });

  const sortBrokerAgencies = brokerAgencies.sort((a, b) =>
    a.label.localeCompare(b.label)
  );

  const { brokers, loading: brokersLoading } = useGetBrokerOptions({
    pageNumber: 0,
    pageSize: 2000,
    enabled: brokerageUuid,
    brokerageUuid,
  });
  const { brokerageAddress } = useGetBrokerageAddress({
    brokerageUuid,
    enabled: brokerageUuid,
  });

  const setBrokerageAddress = useCallback(() => {
    if (brokerageAddress[0]) {
      const {
        primaryStreet1,
        primaryStreet2,
        primaryCity,
        primaryPostalCode,
        primaryStateName,
        primaryCountryName,
      } = brokerageAddress[0];

      setValue(CreateSubmissionField.BrokerStreet1, primaryStreet1);
      setValue(CreateSubmissionField.BrokerStreet2, primaryStreet2);
      setValue(CreateSubmissionField.BrokerCity, primaryCity);
      setValue(CreateSubmissionField.BrokerPostalCode, primaryPostalCode);
      setValue(CreateSubmissionField.BrokerRegionUuid, primaryStateName);
      setValue(CreateSubmissionField.BrokerCountryUuid, primaryCountryName);
    } else {
      setValue(CreateSubmissionField.BrokerStreet1, null);
      setValue(CreateSubmissionField.BrokerStreet2, null);
      setValue(CreateSubmissionField.BrokerCity, null);
      setValue(CreateSubmissionField.BrokerPostalCode, null);
      setValue(CreateSubmissionField.BrokerRegionUuid, null);
      setValue(CreateSubmissionField.BrokerCountryUuid, null);
      setValue(CreateSubmissionField.BrokerAgentUuid, null);
    }
  }, [setValue, brokerageAddress]);

  useEffect(() => {
    setBrokerageAddress();
  }, [brokerageAddress, setBrokerageAddress]);

  const SELECTED_BROKER_OPTION = brokers.map((item) => ({
    label: `${item.firstName} ${item.lastName}`,
    value: item.uuid,
    email: item.email,
    phone: item.phone,
    title: item.title,
    data: {
      email: item.email,
      phone: item.phone,
      title: item.title,
    },
  }));

  // useEffect(() => {
  //   if (selectedBrokerAgent) {
  //     setValue(CreateSubmissionField.BrokerEmailAddress, '');
  //     setValue(CreateSubmissionField.BrokerPhoneNumber, '');
  //   } else {
  //     setValue(CreateSubmissionField.BrokerEmailAddress, null);
  //     setValue(CreateSubmissionField.BrokerPhoneNumber, null);
  //   }
  // }, [selectedBrokerAgent, setValue]);

  return (
    <FormContainerWithHeader title="Brokerage and Broker" showSeparator={false}>
      <FormSearchableSelectField
        minDropdownWidth="430px"
        maxDropdownWidth="600px"
        {...getCreateSubmissionFieldProps(CreateSubmissionField.BrokerageUuid)}
        options={sortBrokerAgencies}
        required
        label={'BROKERAGE'}
        isLoading={brokerageLoading}
        placeholder="Select Brokerage"
      />
      <FormSearchableSelectField
        {...getCreateSubmissionFieldProps(
          CreateSubmissionField.BrokerAgentUuid
        )}
        minDropdownWidth="430px"
        maxDropdownWidth="600px"
        options={SELECTED_BROKER_OPTION || []}
        disabled={
          (!SELECTED_BROKER_OPTION || !SELECTED_BROKER_OPTION.length) &&
          !brokersLoading
        }
        label={'BROKER'}
        required
        isLoading={brokersLoading}
        placeholder="Select Broker"
        onChangeOption={(e) => {
          setValue(
            CreateSubmissionField.BrokerEmailAddress,
            e?.data?.email || ''
          );
          setValue(
            CreateSubmissionField.BrokerPhoneNumber,
            e?.data?.phone || ''
          );
          setValue(CreateSubmissionField.Designation, e?.data?.title || '');
        }}
      />
      <FormViewValueField
        {...getCreateSubmissionFieldProps(CreateSubmissionField.BrokerStreet1)}
        className="readonly-field"
      />
      <FormViewValueField
        {...getCreateSubmissionFieldProps(CreateSubmissionField.BrokerStreet2)}
        className="readonly-field"
      />
      <FormViewValueField
        {...getCreateSubmissionFieldProps(CreateSubmissionField.BrokerCity)}
        className="readonly-field"
      />
      <FormViewValueField
        {...getCreateSubmissionFieldProps(
          CreateSubmissionField.BrokerRegionUuid
        )}
        className="readonly-field"
      />
      <FormViewValueField
        {...getCreateSubmissionFieldProps(
          CreateSubmissionField.BrokerPostalCode
        )}
        className="readonly-field"
      />
      <FormViewValueField
        {...getCreateSubmissionFieldProps(
          CreateSubmissionField.BrokerCountryUuid
        )}
        className="readonly-field"
      />
      <FormViewValueField
        {...getCreateSubmissionFieldProps(
          CreateSubmissionField.BrokerPhoneNumber
        )}
        className="readonly-field"
      />
      <FormViewValueField
        {...getCreateSubmissionFieldProps(
          CreateSubmissionField.BrokerEmailAddress
        )}
        className="readonly-field"
      />
      <FormViewValueField
        {...getCreateSubmissionFieldProps(CreateSubmissionField.Designation)}
        className="readonly-field"
      />
    </FormContainerWithHeader>
  );
};

const UnderwriterSection = () => {
  const { data: underwriterList, loading } = useGetUsers({
    userGroups: [UserGroup.Underwriter],
  });
  const UNDERWRITER_OPTIONS = useMemo(
    () => getUserOptions(underwriterList),
    [underwriterList]
  );
  return (
    <FormContainerWithHeader title="Underwriter" showSeparator={false}>
      <NewFromSelectField
        name={CreateSubmissionField.UserUuid}
        options={UNDERWRITER_OPTIONS}
        label={'UNDERWRITER'}
        required={true}
        isLoading={loading}
        containerWidth={4}
        placeholder="Select Underwriter"
      />
    </FormContainerWithHeader>
  );
};
const UnderwritingCreateSubmissionRequestedPolicySection = () => {
  const formMethods = useFormContext();

  const { entities: partners = [] } = useGetEntities({
    isProgramPartner: true,
  });

  const PARTNER_OPTIONS = [
    { value: 'No Partner', label: 'No Partner' },
    ...partners.map((partner) => ({
      value: partner?.uuid ?? '',
      label: partner?.name ?? 'N/A',
    })),
  ];

  const policyStartDate = formMethods.watch(
    CreateSubmissionField.PolicyStartDate
  );

  useEffect(() => {
    if (!(policyStartDate === undefined || policyStartDate === null)) {
      formMethods.setValue(
        CreateSubmissionField.PolicyEndDate,
        addYear(policyStartDate)
      );
      formMethods.clearErrors(CreateSubmissionField.PolicyEndDate);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [policyStartDate]);

  return (
    <FormContainerWithHeader title="Requested Policy" showSeparator={false}>
      <FormSearchableSelectField
        {...getCreateSubmissionFieldProps(CreateSubmissionField.PartnerUuid)}
        options={PARTNER_OPTIONS}
        placeholder="Select Partner"
        minDropdownWidth="430px"
        maxDropdownWidth="600px"
      />
      <FormSearchableSelectField
        {...getCreateSubmissionFieldProps(CreateSubmissionField.ProductType)}
        options={PRODUCT_TYPE_OPTIONS}
        required
        placeholder="Select Product"
        minDropdownWidth="430px"
        maxDropdownWidth="600px"
      />
      <FormSearchableSelectField
        {...getCreateSubmissionFieldProps(CreateSubmissionField.Type)}
        options={submissionTypes}
        required
        placeholder="Select Policy Type"
        minDropdownWidth="430px"
        maxDropdownWidth="600px"
      />
      <FormNumberInputAdornmentField
        {...getCreateSubmissionFieldProps(CreateSubmissionField.AggregateLimit)}
        adornmentText="$"
        thousandSeparator=","
        placeholder="0"
        isAllowed={(values) => checkPositiveNumber(values)}
      />
      <FormNumberInputAdornmentField
        {...getCreateSubmissionFieldProps(
          CreateSubmissionField.PolicyRetention
        )}
        adornmentText="$"
        thousandSeparator=","
        placeholder="0"
        isAllowed={(values) => checkPositiveNumber(values)}
      />
      <NewDateField
        {...getCreateSubmissionFieldProps(
          CreateSubmissionField.PolicyStartDate
        )}
        placeholder="Select Effective Date"
      />

      <NewDateField
        {...getCreateSubmissionFieldProps(CreateSubmissionField.PolicyEndDate)}
        placeholder="Select Expiration Date"
      />
    </FormContainerWithHeader>
  );
};

const UnderwritingCreateSubmissionSubmissionSection = () => {
  const formMethods = useFormContext();
  const receivedAt = formMethods.watch(CreateSubmissionField.ReceivedAt);

  const today = new Date().toISOString();
  const dateMoment = moment(today);

  useEffect(() => {
    if (!receivedAt) {
      formMethods.setValue(CreateSubmissionField.ReceivedAt, dateMoment);
      return;
    }
    formMethods.setValue(
      CreateSubmissionField.DueDate,
      addDays(receivedAt, 10)
    );
  }, [receivedAt, formMethods, dateMoment]);

  return (
    <FormContainerWithHeader title="Submission" showSeparator={false}>
      <GridLayout
        container
        item
        xs={12}
        sm={4}
        className="submission-inner-grid"
      >
        <NewDateField
          {...getCreateSubmissionFieldProps(CreateSubmissionField.ReceivedAt)}
          required
          containerWidth={12}
          placeholder="Select Received Date"
        />
        <NewFormTextField
          {...getCreateSubmissionFieldProps(CreateSubmissionField.ReceivedBy)}
          required
          containerWidth={12}
          placeholder="Enter Email Address"
        />
        <NewDateField
          {...getCreateSubmissionFieldProps(CreateSubmissionField.DueDate)}
          containerWidth={12}
          placeholder="Select Due Date"
        />
      </GridLayout>
      <GridLayout item xs={12} sm={8}>
        <FormRichTextField
          {...getCreateSubmissionFieldProps(CreateSubmissionField.Notes)}
          containerWidth={12}
        />
      </GridLayout>
      <FileUploader
        onFileHandler={({ allAcceptedFiles }: IUploadOnDropProps) => {
          formMethods.setValue(
            CreateSubmissionField.Attachments,
            allAcceptedFiles
          );
        }}
        onFileRemove={(acceptedFiles) => {
          formMethods.setValue(
            CreateSubmissionField.Attachments,
            acceptedFiles
          );
        }}
        label="Attachments"
        progressBar={false}
        showRemoveIcon={true}
        verb="attach"
        enableFolderSelection
      />
    </FormContainerWithHeader>
  );
};

function createSubmissionFieldsToVariables({
  productType,
  ...fields
}: CreateSubmissionFields): SubmissionInput {
  const input: SubmissionInput = {
    legalName: fields.legalName,
    tradingName: fields.tradingName,
    domain: fields.domain,
    street1: fields.street1 ?? '',
    street2: fields.street2 ?? '',
    city: fields.city,
    regionUuid: fields.regionUuid,
    postalCode: fields.postalCode,
    countryUuid: fields.countryUuid,
    contactFirstName: fields.contactFirstName,
    contactLastName: fields.contactLastName,
    contactEmailAddress: fields.contactEmailAddress,
    contactPhoneNumber: fields.contactPhoneNumber,
    annualRevenue: Number(fields.annualRevenue ?? 0),
    yearEstablished: (fields.yearEstablished ?? 0).toString(),
    primaryFundamental: {
      revenue: Number(fields.annualRevenue ?? 0),
      projectedRevenue: Number(fields.projectedRevenue ?? 0),
      projectedForeignRevenue: Number(fields.projectedForeignRevenue ?? 0),
      foreignRevenue: Number(fields.foreignRevenue ?? 0),
      ratedRevenueOverride: Number(fields.ratedRevenueOverride ?? 0),
    },
    numEmployees: Number(fields.numEmployees ?? 0),
    industrySector: fields.industrySector,
    type: fields.type,
    aggregateLimit: Number(fields.aggregateLimit ?? 0),
    policyRetention: Number(fields.policyRetention ?? 0),
    policyStartDate: fields.policyStartDate,
    policyEndDate: fields.policyEndDate,
    receivedAt: fields.receivedAt,
    receivedBy: fields.receivedBy,
    dueDate: fields.dueDate,
    notes: fields.notes,
    brokerUuid: fields.brokerUuid,
    brokerAgentUuid: fields.brokerAgentUuid,

    partnerUuid: fields.partner === 'No Partner' ? null : fields.partner,
    productUuid: fields.productUuid ?? DEFAULT_PRODUCT_TYPE,
    ...(fields.attachments
      ? {
          attachments: {
            files: (fields.attachments as unknown as IUploadFile[]).map(
              (attachment): AttachmentUploadInput => {
                return {
                  file: attachment.file,
                  folder: attachment.folder,
                };
              }
            ),
          } as unknown as Attachments,
        }
      : {}),
    userUuid: fields.userUuid,
  };

  convergeLogger.debug('Create submission data: ', input);
  return input;
}
